import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Typography, Stack, TextField, Button, Chip } from '@mui/material';
import PropTypes from 'prop-types';
import apiAnswersReportSlice, { useCreateAnswerAbuseReportMutation } from '../features/api/rest/apiAnswersReportsSlice';
import Spinner from './Spinner';

const reportTypes = ['VIOLENCE', 'HATE', 'BULLY', 'SEX', 'SPAM', 'MISINFORMATION', 'FRAUD', 'OTHER'];

function AnswerReportCreate({ questionId, answerId }) {
  const { ean } = useParams();
  const { t } = useTranslation();
  const { data: fetchedReports } = apiAnswersReportSlice.endpoints.listAnswerAbuseReports.useQueryState({ ean, questionId, answerId });
  const [reports, setReports] = React.useState(reportTypes.map((item) => ({ type: item, note: '' })));
  const [activeReportType, setActiveReportType] = React.useState(reports[0]?.type);
  const [createAnswerReport, { isLoading }] = useCreateAnswerAbuseReportMutation();

  React.useEffect(() => {
    const newReports = reportTypes.filter((reportType) => !fetchedReports.map((item) => item.type).includes(reportType)).map((item) => ({ type: item, note: '' }));
    setReports(newReports);
    setActiveReportType(newReports[0]?.type);
  }, [fetchedReports]);

  const changeReportNote = (newNote) => {
    setReports((prevReports) => prevReports.map((item) => (item.type === activeReportType ? { ...item, note: newNote } : item)));
  };

  const handleSubmit = () => {
    const reportToCreate = reports.filter((item) => item.type === activeReportType)[0];
    createAnswerReport({ ean, questionId, answerId, payload: { type: reportToCreate.type, note: reportToCreate.note } });
  };

  const reportChip = (reportType) => (
    <Chip
      key={reportType}
      label={t(`reports.types.${reportType}`)}
      onClick={() => setActiveReportType(reportType)}
      disabled={isLoading}
      sx={{ backgroundColor: activeReportType === reportType ? 'primary.main' : 'grey.main', color: activeReportType === reportType ? 'primary.contrastText' : 'grey.700', '&:hover': { backgroundColor: 'primary.main', color: 'primary.contrastText' } }}
    />
  );

  return (
    <>
      <Typography variant="body3">{t('reports.create.description')}</Typography>
      <Stack direction="row" sx={{ my: 3 }} flexWrap="wrap" gap={1}>
        {reports.map(({ type }) => reportChip(type))}
      </Stack>
      <TextField
        sx={{ mb: 2 }}
        fullWidth
        multiline
        rows={4}
        variant="outlined"
        placeholder={t('reports.create.note.placeholder')}
        value={reports.find((item) => item.type === activeReportType)?.note}
        onChange={(e) => changeReportNote(e.target.value)}
        disabled={isLoading}
      />
      <Button variant="outlined" color="error" onClick={handleSubmit} disabled={isLoading} sx={{ mb: 2 }}>
        {t('reports.actions.add')}
      </Button>
      {isLoading && <Spinner removeBox />}
    </>
  );
}

AnswerReportCreate.propTypes = {
  answerId: PropTypes.number.isRequired,
  questionId: PropTypes.number.isRequired,
};

export default AnswerReportCreate;
